import React, { useEffect, useState } from "react";
import ProfilePageContent from "../../components/lawyers/ProfilePageContent";
import LawyerProfileCard from "../../components/lawyers/LawyerProfileCard";
import LawyerProfileDescription from "../../components/lawyers/LawyerProfileDescription";
import LawyerContactCard from "../../components/lawyers/LawyerContactCard";
import LawyerHours from "../../components/lawyers/LawyerHours";
import OtherLawyers from "../../components/lawyers/OtherLawyers";
import NotFound from "../NotFound/NotFound";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import { useLawyerContext } from "../../shared/context/LawyerContext";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import useCanonical from "../../shared/seo/useCanonical";
import {
  formatPhone,
  hasProfilePhoto,
  lawyerAddress,
  lawyerGovernorate,
  lawyerName,
  lawyerState,
} from "../../components/lawyers/lawyerHelpers";

function structuredData(lawyer, lang, t) {
  const canonical = urls.absoluteUrl(window.location.origin, urls.lawyerPath(lawyer, lang));
  const governorate = lawyerGovernorate(lawyer);
  const phone = formatPhone(lawyer.phoneNumber);
  const crumbs = [
    { name: t("Home"), path: urls.homePath(lang) },
    { name: t("Lawyers"), path: urls.lawyersPath(lang) },
    governorate && { name: urls.governorateName(governorate, lang), path: urls.lawyersPath(lang, { governorate }) },
    { name: lawyerName(lawyer, lang), path: urls.lawyerPath(lawyer, lang) },
  ].filter(Boolean);
  return [
    {
      "@context": "https://schema.org",
      "@type": "Attorney",
      name: copy.lawyer(lang, lawyer).h1,
      url: canonical,
      image: hasProfilePhoto(lawyer) ? lawyer.profilePicture : undefined,
      telephone: phone ? phone.href.replace(/^tel:/, "") : undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: lawyerAddress(lawyer, lang) || undefined,
        addressLocality: lawyerState(lawyer, lang) || undefined,
        addressCountry: "TN",
      },
      areaServed: governorate ? urls.governorateName(governorate, lang) : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        item: urls.absoluteUrl(window.location.origin, item.path),
      })),
    },
  ];
}

export default function Lawyer() {
  const { t } = useTranslation();
  const lang = currentLang();
  const { getLawyerById, getLawyersByFilter } = useLawyerContext();
  const [isLoading, setIsLoading] = useState(true);
  // Not named "NotFound": that would shadow the NotFound page component.
  const [notFound, setNotFound] = useState(false);
  const [lawyer, setLawyer] = useState({});
  const { id, username } = useParams();
  useEffect(() => {
    // Show the found lawyer, or the not-found page when the request fails
    // (axiosFactory then returns the error message instead of data).
    function show(found) {
      // verified === false: retired from the directory (no longer listed by
      // the bar); by-id still returns it, the public page must not.
      if (found && found.verified !== false) {
        setLawyer(found);
        setIsLoading(false);
      } else {
        setNotFound(true);
      }
    }
    async function fetchSubscribedLawyerData() {
      let res = await getLawyersByFilter({
        filters: [
          {
            username: username,
          },
        ],
      });
      show(res.status == 200 ? res.data?.data?.[0] : null);
    }
    async function fetchData() {
      let res = await getLawyerById({ id: id });
      show(res.data?.data);
    }
    window.scrollTo(0, 0);
    setIsLoading(true);
    setNotFound(false);
    if (username) {
      fetchSubscribedLawyerData();
    } else {
      fetchData();
    }
  }, [id, username]);

  const loaded = !isLoading && lawyer.id;
  const canonical = loaded ? urls.lawyerPath(lawyer, lang) : null;
  // A vanity /<username> URL stays in the address bar; its canonical tag
  // still points at the profile URL.
  useCanonical(username ? null : canonical);

  if (notFound) return <NotFound />;
  const text = loaded ? copy.lawyer(lang, lawyer) : null;

  return (
    <>
      {loaded ? (
        <Seo
          title={text.title}
          description={text.description}
          path={canonical}
          alternates={urls.alternates((l) => urls.lawyerPath(lawyer, l))}
          image={hasProfilePhoto(lawyer) ? lawyer.profilePicture : undefined}
          type="profile"
          jsonLd={structuredData(lawyer, lang, t)}
        />
      ) : (
        <Seo title={copy.lawyers(lang).title} />
      )}
      {isLoading ? (
        <ProfilePageContent>
          <ComponentsLoader />
        </ProfilePageContent>
      ) : (
        <>
          <LawyerProfileCard lawyer={lawyer} />
          <ProfilePageContent>
            <div className="col-lg-8">
              <LawyerProfileDescription lawyer={lawyer} />
              <OtherLawyers lawyer={lawyer} />
            </div>
            {/* Contact first on phones, in the side column on desktop. */}
            <aside className="col-lg-4 order-first order-lg-last">
              <div className="avc-profile-aside">
                <LawyerContactCard lawyer={lawyer} />
                <LawyerHours lawyer={lawyer} />
              </div>
            </aside>
          </ProfilePageContent>
        </>
      )}
    </>
  );
}
